import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import axios from 'axios';
import { KmSummaryCard } from '../components/KmSummaryCard';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  DollarSign, TrendingUp, TrendingDown, Truck, Fuel,
  Package, Users, ChevronDown, Bot, AlertTriangle,
  CheckCircle, Star, Wrench, Gauge, ArrowRight, Loader2, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: '7d', label: '7 jours' },
  { value: '30d', label: '30 jours' },
  { value: '90d', label: '90 jours' },
  { value: '12m', label: '12 mois' },
  { value: 'ytd', label: 'Annee en cours' },
];

const TABS = [
  { id: 'global', label: 'Global', icon: Package },
  { id: 'drivers', label: 'Par Chauffeur', icon: Users },
  { id: 'vehicles', label: 'Par Vehicule', icon: Truck },
  { id: 'ai', label: 'Analyse IA', icon: Bot },
];

// ==================== SHARED COMPONENTS ====================

const KpiCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition-colors" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between mb-2">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon size={18} style={{ color }} />
      </div>
    </div>
    <p className="text-2xl font-black text-white">{value}</p>
    <p className="text-xs text-slate-400 mt-0.5">{label}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
  </div>
);

const ChartCard = ({ title, children, className = '' }) => (
  <div className={`bg-slate-900/80 border border-slate-800 rounded-xl p-5 ${className}`}>
    <h3 className="text-sm font-semibold text-slate-300 mb-4">{title}</h3>
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-xl">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-semibold" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

const ScoreBadge = ({ score, size = 'md' }) => {
  const color = score >= 70 ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' :
                score >= 40 ? 'text-amber-400 bg-amber-400/10 border-amber-400/20' :
                'text-red-400 bg-red-400/10 border-red-400/20';
  const sz = size === 'lg' ? 'w-12 h-12 text-lg' : 'w-8 h-8 text-xs';
  return (
    <div className={`${sz} rounded-full border flex items-center justify-center font-bold ${color}`}>
      {score}
    </div>
  );
};

// ==================== GLOBAL TAB ====================

const GlobalTab = ({ data }) => {
  const k = data.kpis;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={DollarSign} label="Revenus" value={`${k.revenue.toLocaleString()}$`} sub={`${k.invoices_count} factures | ${k.unpaid_amount.toLocaleString()}$ impaye`} color="#f59e0b" />
        <KpiCard icon={Package} label="Livraisons" value={k.deliveries_completed} sub={`${k.delivery_completion_rate}% completion | ${k.deliveries_pending} en attente`} color="#34d399" />
        <KpiCard icon={Truck} label="Kilometres" value={`${k.total_km.toLocaleString()} km`} sub={`${k.report_count} rapports | ${k.vehicle_count} vehicules`} color="#60a5fa" />
        <KpiCard icon={Fuel} label="Depenses" value={`${k.total_expenses.toLocaleString()}$`} sub={`Carburant: ${k.fuel_expenses.toLocaleString()}$ (${k.fuel_liters}L)`} color="#f87171" />
      </div>

      <div className={`rounded-xl p-4 border flex items-center justify-between ${k.profit >= 0 ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`} data-testid="profit-banner">
        <div className="flex items-center gap-3">
          {k.profit >= 0 ? <TrendingUp className="text-emerald-400" size={22} /> : <TrendingDown className="text-red-400" size={22} />}
          <div>
            <p className="text-xs text-slate-400">Profit net</p>
            <p className={`text-xl font-black ${k.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {k.profit >= 0 ? '+' : ''}{k.profit.toLocaleString()}$
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Revenus vs Depenses" className="lg:col-span-2">
          {data.revenue_chart.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.revenue_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenus" name="Revenus ($)" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="depenses" name="Depenses ($)" fill="#f87171" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[240px] flex items-center justify-center text-slate-500 text-sm">Aucune donnee</div>}
        </ChartCard>
        <ChartCard title="Statut des livraisons">
          {data.dispatch_chart.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.dispatch_chart} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                  {data.dispatch_chart.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span className="text-slate-300">{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="h-[240px] flex items-center justify-center text-slate-500 text-sm">Aucune livraison</div>}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Kilometres parcourus">
          {data.km_chart.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data.km_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="km" name="Km" stroke="#60a5fa" strokeWidth={2.5} dot={{ fill: '#60a5fa', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="h-[200px] flex items-center justify-center text-slate-500 text-sm">Aucune donnee</div>}
        </ChartCard>
        <ChartCard title="Depenses par categorie">
          {data.expense_chart.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.expense_chart} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis dataKey="name" type="category" width={100} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="montant" name="Montant ($)" fill="#a78bfa" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[200px] flex items-center justify-center text-slate-500 text-sm">Aucune depense</div>}
        </ChartCard>
      </div>

      {data.driver_activity.length > 0 && (
        <ChartCard title="Activite des chauffeurs">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-800">
                <th className="text-left py-2 px-3 text-slate-400 font-medium">Chauffeur</th>
                <th className="text-right py-2 px-3 text-slate-400 font-medium">Rapports</th>
                <th className="text-right py-2 px-3 text-slate-400 font-medium">Arrets</th>
                <th className="text-right py-2 px-3 text-slate-400 font-medium">Km</th>
              </tr></thead>
              <tbody>
                {data.driver_activity.map((d, i) => (
                  <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="py-2 px-3 text-white font-medium flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center text-xs text-amber-500 font-bold shrink-0">{d.name.charAt(0)}</div>
                      {d.name}
                    </td>
                    <td className="text-right py-2 px-3 text-slate-300">{d.rapports}</td>
                    <td className="text-right py-2 px-3 text-slate-300">{d.arrets}</td>
                    <td className="text-right py-2 px-3 text-slate-300">{d.km.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}
    </div>
  );
};

// ==================== DRIVERS TAB ====================

const DriversTab = ({ token, period }) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/api/analytics/drivers?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDrivers(res.data.drivers || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [token, period]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-500" size={32} /></div>;

  return (
    <div className="space-y-4" data-testid="drivers-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {drivers.map((d) => (
          <div key={d.driver_id} className="bg-slate-900/80 border border-slate-800 rounded-xl p-5 hover:border-slate-700 transition-colors">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-amber-500 font-bold">
                  {d.name.charAt(0)}
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{d.name}</p>
                  <p className="text-slate-500 text-xs">Depuis {d.first_report || '?'}</p>
                </div>
              </div>
              <ScoreBadge score={d.efficiency_score} size="lg" />
            </div>

            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center">
                <p className="text-lg font-bold text-white">{d.rapports}</p>
                <p className="text-[10px] text-slate-500">Rapports</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-blue-400">{d.km.toLocaleString()}</p>
                <p className="text-[10px] text-slate-500">Km total</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-emerald-400">{d.stops}</p>
                <p className="text-[10px] text-slate-500">Arrets</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-800">
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-300">{d.km_per_report}</p>
                <p className="text-[10px] text-slate-500">Km/rapport</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-300">{d.stops_per_report}</p>
                <p className="text-[10px] text-slate-500">Arrets/rapport</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-300">{d.hours}h</p>
                <p className="text-[10px] text-slate-500">Heures</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {drivers.length === 0 && <p className="text-center text-slate-500 py-10">Aucun chauffeur avec des rapports</p>}
    </div>
  );
};

// ==================== VEHICLES TAB ====================

const VehiclesTab = ({ token, period }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/analytics/vehicles?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVehicles(res.data.vehicles || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchVehicles(); }, [token, period]);

  const hideVehicle = async (vehicleId) => {
    try {
      await axios.patch(`${API}/api/analytics/vehicles/${vehicleId}/hide`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVehicles(prev => prev.filter(v => v.id !== vehicleId));
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-500" size={32} /></div>;

  const alertColors = {
    critical: 'border-red-500/30 bg-red-500/5',
    warning: 'border-amber-500/30 bg-amber-500/5',
    normal: 'border-slate-800',
  };

  return (
    <div className="space-y-4" data-testid="vehicles-tab">
      <KmSummaryCard token={token} />
      {vehicles.some(v => v.alert === 'critical') && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="text-red-400 shrink-0" size={20} />
          <p className="text-sm text-red-300">
            <strong>{vehicles.filter(v => v.alert === 'critical').length} vehicule(s)</strong> necessitent une attention immediate (age eleve, score de sante bas)
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800">
            <th className="text-left py-3 px-3 text-slate-400 font-medium">Vehicule</th>
            <th className="text-center py-3 px-3 text-slate-400 font-medium">Sante</th>
            <th className="text-right py-3 px-3 text-slate-400 font-medium">Age</th>
            <th className="text-right py-3 px-3 text-slate-400 font-medium">Odometre</th>
            <th className="text-right py-3 px-3 text-slate-400 font-medium">Km (12 mois)</th>
            <th className="text-right py-3 px-3 text-slate-400 font-medium">Coût / km</th>
            <th className="text-right py-3 px-3 text-slate-400 font-medium">Maintenances</th>
            <th className="text-right py-3 px-3 text-slate-400 font-medium">Couts</th>
            <th className="text-center py-3 px-3 text-slate-400 font-medium">Statut</th>
            <th className="text-center py-3 px-2 text-slate-400 font-medium w-10"></th>
          </tr></thead>
          <tbody>
            {vehicles.map((v, i) => (
              <Fragment key={v.id}>
              {v.owner_key && v.owner_key !== 'own' && v.owner_key !== vehicles[i - 1]?.owner_key && (
                <tr data-testid={`analytics-owner-section-${v.owner_key}`}>
                  <td colSpan={10} className="pt-6 pb-2 px-3 text-xs font-semibold uppercase tracking-wider text-amber-400">{v.owner_label}</td>
                </tr>
              )}
              <tr className={`border-b border-slate-800/50 hover:bg-slate-800/20 transition-colors ${alertColors[v.alert]}`}>
                <td className="py-3 px-3">
                  <div className="flex items-center gap-2">
                    <Truck size={16} className={v.vehicle_type === 'trailer' ? 'text-purple-400' : 'text-blue-400'} />
                    <div>
                      <p className="text-white font-semibold">{v.unit_number}</p>
                      <p className="text-[11px] text-slate-500">{v.brand} {v.model} {v.year}</p>
                    </div>
                  </div>
                </td>
                <td className="text-center py-3 px-3"><ScoreBadge score={v.health_score} /></td>
                <td className="text-right py-3 px-3 text-slate-300">{v.age} ans</td>
                <td className="text-right py-3 px-3 text-slate-300">{v.odometer ? v.odometer.toLocaleString() + ' km' : '-'}</td>
                <td className="text-right py-3 px-3 text-slate-300" data-testid={`analytics-vehicle-km-${v.id}`} title={v.km_source === 'gps' ? 'Calculé via GPS des rapports' : v.km_source === 'odometre' ? "Écart entre relevés de l'atelier" : undefined}>{v.km_12m ? v.km_12m.toLocaleString() + ' km' : '-'}</td>
                <td className="text-right py-3 px-3 text-slate-300" data-testid={`analytics-vehicle-cpk-${v.id}`} title={v.cost_per_km != null ? `${(v.cost_12m || 0).toLocaleString('fr-CA')} $ de réparations et entretiens ÷ ${v.km_12m.toLocaleString('fr-CA')} km (12 mois)` : undefined}>{v.cost_per_km != null ? v.cost_per_km.toFixed(2) + ' $/km' : '-'}</td>
                <td className="text-right py-3 px-3 text-slate-300">{v.maintenance_count}</td>
                <td className="text-right py-3 px-3 text-slate-300" data-testid={`analytics-vehicle-cost-${v.id}`} title={v.work_orders_count ? `Bons de travail : pièces ${v.work_orders_parts_cost}$ + main-d'oeuvre ${v.work_orders_labor_cost}$ (${v.work_orders_count} bon(s))` : undefined}>
                  {v.maintenance_cost > 0 ? v.maintenance_cost.toLocaleString() + '$' : '-'}
                  {v.work_orders_count > 0 && <span className="block text-[10px] text-slate-500">dont {v.work_orders_count} bon(s) atelier</span>}
                </td>
                <td className="text-center py-3 px-3">
                  {v.alert === 'critical' && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full font-semibold">Critique</span>}
                  {v.alert === 'warning' && <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full font-semibold">Attention</span>}
                  {v.alert === 'normal' && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full font-semibold">OK</span>}
                </td>
                <td className="text-center py-3 px-2">
                  <button
                    onClick={() => hideVehicle(v.id)}
                    className="p-1 rounded hover:bg-red-500/20 text-slate-600 hover:text-red-400 transition-colors"
                    title="Retirer de l'analytics"
                    data-testid={`hide-vehicle-${v.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {vehicles.length === 0 && <p className="text-center text-slate-500 py-10">Aucun vehicule</p>}
    </div>
  );
};

// ==================== AI TAB ====================

const AITab = ({ token }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API}/api/analytics/ai-insights`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
      setInsights(res.data.insights);
    } catch (e) {
      setError('Erreur lors de l\'analyse IA. Reessayez.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!insights && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16" data-testid="ai-tab">
        <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-4">
          <Bot size={32} className="text-amber-500" />
        </div>
        <h3 className="text-lg font-bold text-white mb-2">Analyse IA de votre flotte</h3>
        <p className="text-slate-400 text-sm text-center max-w-md mb-6">
          L'IA analysera vos chauffeurs, vehicules, depenses et livraisons pour vous donner des recommandations personnalisees.
        </p>
        <button
          onClick={fetchInsights}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold px-6 py-3 rounded-xl transition-colors"
          data-testid="ai-analyze-btn"
        >
          <Bot size={18} /> Lancer l'analyse
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="animate-spin text-amber-500 mb-4" size={40} />
        <p className="text-slate-400 text-sm">L'IA analyse vos donnees...</p>
        <p className="text-slate-500 text-xs mt-1">Cela peut prendre 15-30 secondes</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-red-400 mb-4">{error}</p>
        <button onClick={fetchInsights} className="text-amber-500 underline text-sm">Reessayer</button>
      </div>
    );
  }

  const ai = insights;

  return (
    <div className="space-y-4" data-testid="ai-insights">
      {/* Summary */}
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <Bot size={18} className="text-amber-500" />
          <h3 className="text-sm font-semibold text-amber-400">Resume executif</h3>
        </div>
        <p className="text-slate-300 text-sm leading-relaxed">{ai.resume}</p>
      </div>

      {/* Strengths */}
      {ai.points_forts?.length > 0 && (
        <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-emerald-400 flex items-center gap-2 mb-3">
            <CheckCircle size={16} /> Points forts
          </h3>
          <ul className="space-y-2">
            {ai.points_forts.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                <Star size={12} className="text-emerald-400 mt-1 shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Alerts */}
      {ai.alertes?.length > 0 && (
        <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-red-400 flex items-center gap-2 mb-3">
            <AlertTriangle size={16} /> Alertes
          </h3>
          <div className="space-y-3">
            {ai.alertes.map((a, i) => {
              const titre = typeof a === 'string' ? a : (a.titre || '');
              const desc = typeof a === 'string' ? '' : (a.description || '');
              const priorite = typeof a === 'string' ? 'basse' : (a.priorite || 'basse');
              const action = typeof a === 'string' ? '' : (a.action || '');
              return (
              <div key={i} className={`rounded-lg p-3 border ${priorite === 'haute' ? 'border-red-500/30 bg-red-500/5' : priorite === 'moyenne' ? 'border-amber-500/30 bg-amber-500/5' : 'border-slate-700 bg-slate-800/30'}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-white font-semibold text-sm">{titre}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${priorite === 'haute' ? 'bg-red-500/20 text-red-400' : priorite === 'moyenne' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'}`}>
                    {priorite?.toUpperCase()}
                  </span>
                </div>
                {desc && <p className="text-slate-400 text-xs">{desc}</p>}
                {action && (
                  <p className="text-amber-400 text-xs mt-2 flex items-center gap-1">
                    <ArrowRight size={10} /> {action}
                  </p>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Driver insights */}
      {ai.chauffeurs && (ai.chauffeurs.meilleur || ai.chauffeurs.a_suivre) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ai.chauffeurs.meilleur && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
              <p className="text-xs text-emerald-400 font-semibold mb-1 flex items-center gap-1"><Star size={12} /> Meilleur chauffeur</p>
              <p className="text-sm text-slate-300">{ai.chauffeurs.meilleur}</p>
            </div>
          )}
          {ai.chauffeurs.a_suivre && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
              <p className="text-xs text-amber-400 font-semibold mb-1 flex items-center gap-1"><AlertTriangle size={12} /> A suivre</p>
              <p className="text-sm text-slate-300">{ai.chauffeurs.a_suivre}</p>
            </div>
          )}
        </div>
      )}

      {/* Vehicle insights */}
      {ai.vehicules && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ai.vehicules.a_remplacer?.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
              <p className="text-xs text-red-400 font-semibold mb-2 flex items-center gap-1"><Wrench size={12} /> Vehicules a remplacer</p>
              <ul className="space-y-1">
                {ai.vehicules.a_remplacer.map((v, i) => <li key={i} className="text-sm text-slate-300">- {typeof v === 'string' ? v : `${v.unite || v.nom || ''} — ${v.raison || v.description || ''}`}</li>)}
              </ul>
            </div>
          )}
          {ai.vehicules.a_surveiller?.length > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
              <p className="text-xs text-amber-400 font-semibold mb-2 flex items-center gap-1"><Gauge size={12} /> Vehicules a surveiller</p>
              <ul className="space-y-1">
                {ai.vehicules.a_surveiller.map((v, i) => <li key={i} className="text-sm text-slate-300">- {typeof v === 'string' ? v : `${v.unite || v.nom || ''} — ${v.raison || v.description || ''}`}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {ai.recommandations?.length > 0 && (
        <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-blue-400 flex items-center gap-2 mb-3">
            <TrendingUp size={16} /> Recommandations
          </h3>
          <div className="space-y-3">
            {ai.recommandations.map((r, i) => (
              <div key={i} className="bg-slate-800/40 rounded-lg p-3">
                <p className="text-white font-semibold text-sm mb-1">{r.titre}</p>
                <p className="text-slate-400 text-xs mb-1">{r.description}</p>
                {r.impact_estime && <p className="text-blue-400 text-xs font-medium">Impact: {r.impact_estime}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center pt-2">
        <button onClick={fetchInsights} className="text-slate-500 hover:text-amber-500 text-xs underline transition-colors">
          Relancer l'analyse
        </button>
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================

export default function AnalyticsPage() {
  const { token } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('12m');
  const [tab, setTab] = useState('global');
  const [showPeriodMenu, setShowPeriodMenu] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/analytics/dashboard?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="analytics-loading">
        <div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 px-4 lg:px-6 pt-2" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-white">Analytics</h1>
          <p className="text-sm text-slate-400">Performance en temps reel</p>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowPeriodMenu(!showPeriodMenu)}
            className="flex items-center gap-2 bg-slate-800 border border-slate-700 px-4 py-2 rounded-xl text-sm text-slate-300 hover:text-white transition-colors"
            data-testid="period-selector"
          >
            {PERIODS.find(p => p.value === period)?.label}
            <ChevronDown size={14} />
          </button>
          {showPeriodMenu && (
            <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-20 py-1 min-w-[160px]">
              {PERIODS.map(p => (
                <button key={p.value} onClick={() => { setPeriod(p.value); setShowPeriodMenu(false); }}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${period === p.value ? 'text-amber-500 bg-amber-500/10' : 'text-slate-300 hover:bg-slate-700'}`}
                  data-testid={`period-${p.value}`}
                >{p.label}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900/60 p-1 rounded-xl border border-slate-800 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              tab === t.id ? 'bg-slate-800 text-white shadow' : 'text-slate-400 hover:text-slate-300'
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'global' && <GlobalTab data={data} />}
      {tab === 'drivers' && <DriversTab token={token} period={period} />}
      {tab === 'vehicles' && <VehiclesTab token={token} period={period} />}
      {tab === 'ai' && <AITab token={token} />}
    </div>
  );
}
